import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import { createContentKitRemote } from '@gitbook/contentkit-react';
import {
    useHiveSlateHistoryPlugin,
    useHiveWorkingDocument,
    useHiveWorkingDocumentValue,
} from '@gitbook/hive-react';
import type { Change, Value } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { ContentEditor, type ContentEditorProps } from '../ContentEditor';
import type { BlockConfiguration } from '../contexts';

export const ReusableContentEditor = React.memo(
    React.forwardRef(function ReusableContentEditor(
        props: {
            readOnly?: boolean;
            onChange?: (change: Change) => void;
        } & Partial<ContentEditorProps>,
        ref: React.ForwardedRef<EditorContainer>
    ) {
        const { readOnly = false } = props;

        const hiveWorkingDocument = useHiveWorkingDocument();
        const withCompletions = useFlag('WRITE_WITH_AI');
        assertIsDefined(hiveWorkingDocument);
        const [value, selections, onHiveChange] = useHiveWorkingDocumentValue(hiveWorkingDocument);

        const slateHistoryPlugin = useHiveSlateHistoryPlugin(hiveWorkingDocument);
        const plugins = React.useMemo(() => [slateHistoryPlugin], [slateHistoryPlugin]);

        const onChange = useEventCallback((change: Change) => {
            props.onChange?.(change);
            onHiveChange(change);
        });

        return (
            <ReusableContentEditorWithValue
                {...props}
                ref={ref}
                value={value}
                onChange={onChange}
                selections={selections}
                readOnly={readOnly}
                plugins={plugins}
                withCompletions={withCompletions.isEnabled}
            />
        );
    })
);

export const ReusableContentEditorWithValue = React.memo(
    React.forwardRef(function ReusableContentEditor(
        props: {
            value: Value;
            readOnly: boolean;
            onChange: (change: Change) => void;
            withCompletions: boolean;
        } & Partial<ContentEditorProps>,
        ref: React.ForwardedRef<EditorContainer>
    ) {
        const { value, onChange, readOnly, withCompletions, ...rest } = props;
        const blockConfiguration = React.useMemo<BlockConfiguration>(
            () => ({
                withImages: true,
                withAPIs: true,
                withDrawings: true,
                withFiles: true,
                withCompletions,
            }),
            [withCompletions]
        );

        const integrationBlocksRemote = React.useMemo(() => {
            return createContentKitRemote(() => {
                throw new Error('Integrations are not implemented for reusable content.');
            });
        }, []);

        return (
            <ContentEditor
                ref={ref}
                blocks={blockConfiguration}
                onChange={onChange}
                readOnly={readOnly}
                value={value}
                integrationBlocksContext={null}
                integrationBlocksRemote={integrationBlocksRemote}
                integrationData={null}
                selectedSection={null}
                {...rest}
            />
        );
    })
);
